import type { Ring, Vec2 } from '../geo/types';
import { pointInRing } from '../geo/polygon';
import type { CollisionWorld } from '../nav/types';

/**
 * When a visitor has found a place. On foot they stand at it with nothing between; from a boat
 * the quays keep them further off, and nothing on open water hides a building.
 */
export const REACH = { walkM: 12, boatM: 20, sightM: 1.5 } as const;

/** The point of a ring's edge nearest to `p`, and how far it is; no distance within the ring. */
export function nearestOn(
  ring: Ring,
  p: Vec2
): { readonly point: Vec2; readonly distance: number } {
  if (pointInRing(p, ring)) return { point: p, distance: 0 };
  let point: Vec2 = p;
  let distance = Infinity;
  ring.forEach((a, index) => {
    const b = ring[(index + 1) % ring.length] ?? a;
    const along: Vec2 = [b[0] - a[0], b[1] - a[1]];
    const square = along[0] * along[0] + along[1] * along[1];
    const share =
      square < 1e-10
        ? 0
        : Math.max(0, Math.min(1, ((p[0] - a[0]) * along[0] + (p[1] - a[1]) * along[1]) / square));
    const on: Vec2 = [a[0] + along[0] * share, a[1] + along[1] * share];
    const away = Math.hypot(p[0] - on[0], p[1] - on[1]);
    if (away < distance) {
      distance = away;
      point = on;
    }
  });
  return { point, distance };
}

export function reaches(
  footprint: Ring,
  at: Vec2,
  mode: 'walk' | 'boat',
  collision: CollisionWorld
): boolean {
  const near = nearestOn(footprint, at);
  if (near.distance > (mode === 'boat' ? REACH.boatM : REACH.walkM)) return false;
  if (near.distance === 0 || mode === 'boat') return true;
  /* A walk straight towards it must arrive: a row of houses in between is not standing at it. */
  const end = collision.move(at, [near.point[0] - at[0], near.point[1] - at[1]]);
  return (
    Math.hypot(end[0] - near.point[0], end[1] - near.point[1]) <= collision.radius + REACH.sightM
  );
}
